import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { site } from "@/mocks/site";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setQuery("");
      setMobileOpen(false);
    }
  }

  const isCategoryActive = location.pathname.startsWith("/category");
  const isBlacklistActive = location.pathname.startsWith("/blacklist");
  const isAboutActive = location.pathname.startsWith("/about");
  const isHomeActive = location.pathname === "/";

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="hidden border-b border-background-200 bg-primary-950 md:block">
        <div className="mx-auto flex w-full items-center justify-between px-6 py-1.5 text-xs text-background-200">
          <p className="flex items-center gap-2">
            <i className="ri-time-line text-accent-400"></i>
            <span>每日 08:00 更新 · 全天候收录赛事动态</span>
            <span className="mx-1 text-background-400">|</span>
            <span>当前为前端演示数据，实时数据后续接入</span>
          </p>
          <div className="flex items-center gap-4">
            {site.socials.map((s) => (
              <span key={s.name} className="flex items-center gap-1.5 transition-colors hover:text-accent-400">
                <i className={s.icon}></i>
                <span>{s.name}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full items-center justify-between gap-3 px-4 py-3 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-lg font-extrabold text-background-50">
            51
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-base font-extrabold tracking-tight text-foreground-950 md:text-lg">
              每日大赛
            </span>
            <span className="mt-0.5 text-[10px] font-medium tracking-wide text-foreground-500 md:text-xs">
              赛事资讯 · 黑榜排行
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <HeaderLink to="/" label="首页" active={isHomeActive} />
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isCategoryActive
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              赛事分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-[420px] rounded-lg border border-background-200 bg-background-50 p-2 shadow-lg">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.id}`}
                      className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span className={`flex h-9 w-9 items-center justify-center rounded-md text-lg ${c.accent}`}>
                        <i className={c.icon}></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground-900">{c.name}</span>
                        <span className="text-xs text-foreground-500">查看{c.name}赛事</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <HeaderLink to="/blacklist" label="今日黑榜" active={isBlacklistActive} highlight />
          <HeaderLink to="/about" label="关于我们" active={isAboutActive} />
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:block">
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 focus-within:border-primary-400 focus-within:bg-background-50">
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索赛事、队伍或关键词"
                className="w-32 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-44"
              />
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-xl text-foreground-800 lg:hidden"
            aria-label="打开导航菜单"
          >
            <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="px-4 py-3">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
            >
              <i className="ri-search-line text-base text-foreground-400"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索赛事、队伍或关键词"
                className="flex-1 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50"
              >
                搜索
              </button>
            </form>
          </div>
          <div className="flex flex-col px-2 pb-3">
            <Link to="/" className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100">
              首页
            </Link>
            <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
              赛事分类
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <span className={`flex h-8 w-8 items-center justify-center rounded-md ${c.accent}`}>
                    <i className={c.icon}></i>
                  </span>
                  {c.name}
                </Link>
              ))}
            </div>
            <Link
              to="/blacklist"
              className="mt-1 rounded-md px-3 py-3 text-sm font-semibold text-accent-700 hover:bg-accent-50"
            >
              今日黑榜 mdrs 排行
            </Link>
            <Link to="/about" className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100">
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function HeaderLink({
  to,
  label,
  active,
  highlight,
}: {
  to: string;
  label: string;
  active: boolean;
  highlight?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "bg-primary-100 text-primary-800"
          : highlight
          ? "text-accent-700 hover:bg-accent-50"
          : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
      }`}
    >
      {highlight && <i className="ri-fire-line text-base"></i>}
      {label}
    </Link>
  );
}